import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, ShieldCheck, Storefront, Trophy, Users, Lightning,
  CaretDown, CaretUp, Plus, X, Trash, Check, Copy, Sparkle,
  GameController, Clock, Scales, HandPalm, ArrowRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const AUTHORITY_COLORS = {
  official_platform: '#3B82F6',
  official_publisher: '#10B981',
  verified_shop: '#9D4CDD',
  league_admin: '#F59E0B',
  community_custom: '#6B7280',
  competitive_certified: '#EF4444',
};

const TYPE_ICONS = {
  official: ShieldCheck, simplified: BookOpen, house_rules: Storefront,
  league_rules: Trophy, tournament_rules: Lightning, event_rules: Users,
  scoring_pack: Scales, scenario_pack: Sparkle,
};

const CATEGORY_ICONS = {
  setup: GameController, gameplay: BookOpen, scoring: Scales,
  timing: Clock, tie_breaker: Trophy, event_policy: Lightning,
  dispute_policy: HandPalm, behavior_expectations: Users,
  attendance_policy: Check,
};

// ════════════════════════════════════════
//  RULE PACK VIEWER (for event/shop pages)
// ════════════════════════════════════════

export function RulePackViewer({ targetType, targetId }) {
  const [packs, setPacks] = useState([]);
  const [expandedPack, setExpandedPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetType || !targetId) return;
    ax().get(`/api/rule-packs/assigned/${targetType}/${targetId}`)
      .then(r => setPacks(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [targetType, targetId]);

  if (loading) return null;
  if (packs.length === 0) return null;

  return (
    <div className="mb-4" data-testid="rule-pack-viewer">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <BookOpen size={16} weight="duotone" className="text-[#9D4CDD]" /> Rules for This Event
      </h3>
      <div className="space-y-2">
        {packs.map(pack => {
          const isExpanded = expandedPack === pack.rule_pack_id;
          const Icon = TYPE_ICONS[pack.rule_pack_type] || BookOpen;
          const color = AUTHORITY_COLORS[pack.authority_level] || '#6B7280';

          return (
            <div key={pack.rule_pack_id} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl overflow-hidden" data-testid={`rulepack-${pack.rule_pack_id}`}>
              <button onClick={() => setExpandedPack(isExpanded ? null : pack.rule_pack_id)}
                className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-white/[0.02] transition-colors">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
                  <Icon size={18} weight="duotone" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-semibold truncate">{pack.name}</p>
                    <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold shrink-0"
                      style={{ backgroundColor: `${color}15`, color }}>
                      {pack.authority_level?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-[#555] text-[10px] mt-0.5">
                    {pack.rule_pack_type?.replace(/_/g, ' ')} · {pack.item_count} rules
                    {pack.assignment_type !== 'default' && ` · ${pack.assignment_type}`}
                  </p>
                </div>
                {isExpanded ? <CaretUp size={16} className="text-[#555]" /> : <CaretDown size={16} className="text-[#555]" />}
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="border-t border-white/[0.04] px-4 py-3 space-y-2">
                      {pack.description && <p className="text-[#888] text-xs mb-2">{pack.description}</p>}
                      {pack.items?.map((item, i) => {
                        const CatIcon = CATEGORY_ICONS[item.category] || BookOpen;
                        return (
                          <div key={item.item_key || i} className="flex gap-2.5">
                            <CatIcon size={14} className="text-[#444] mt-0.5 shrink-0" />
                            <div>
                              <p className="text-white text-xs font-semibold">{item.title}</p>
                              <p className="text-[#888] text-[11px] leading-relaxed">{item.body}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ════════════════════════════════════════
//  SHOP RULE PACK MANAGER (shop portal)
// ════════════════════════════════════════

export function ShopRulePackManager() {
  const { shopId } = useParams();
  const [packs, setPacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createName, setCreateName] = useState('');
  const [createTemplate, setCreateTemplate] = useState('');
  const [createGame, setCreateGame] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      ax().get(`/api/rule-packs?owner_entity_id=${shopId}`).catch(() => ({ data: [] })),
      ax().get('/api/rule-packs/templates/list').catch(() => ({ data: [] })),
    ]).then(([packsRes, tmplRes]) => {
      setPacks(packsRes.data || []);
      setTemplates(tmplRes.data || []);
    }).finally(() => setLoading(false));
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const createPack = async () => {
    if (!createName.trim()) return;
    try {
      await ax().post('/api/rule-packs', {
        name: createName.trim(),
        game_name: createGame || undefined,
        rule_pack_type: 'house_rules',
        template_type: createTemplate || undefined,
      });
      toast.success('Rule pack created!');
      setShowCreate(false);
      setCreateName('');
      setCreateTemplate('');
      setCreateGame('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const assignToShop = async (packId) => {
    try {
      await ax().post(`/api/rule-packs/${packId}/assign`, {
        target_entity_type: 'shop', target_entity_id: shopId, assignment_type: 'default',
      });
      toast.success('Assigned to shop');
    } catch { toast.error('Failed'); }
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D4CDD]/40 focus:outline-none placeholder:text-[#444]";

  return (
    <div data-testid="shop-rule-pack-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Rule Packs</h2>
          <p className="text-[13px] text-[#555] mt-1">Manage house rules for your shop and events</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold"
          data-testid="create-rule-pack-btn">
          <Plus size={14} className="inline mr-1" /> New Pack
        </button>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCreate(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#0e0e16] border border-white/[0.1] rounded-xl p-5 w-full max-w-sm" data-testid="create-pack-modal">
              <h3 className="text-white font-bold text-base mb-4">Create Rule Pack</h3>
              <div className="space-y-3">
                <input className={inputCls} placeholder="Rule pack name" value={createName} onChange={e => setCreateName(e.target.value)} data-testid="pack-name-input" />
                <input className={inputCls} placeholder="Game name (optional)" value={createGame} onChange={e => setCreateGame(e.target.value)} />
                <div>
                  <p className="text-[#888] text-xs mb-2">Start from template (optional):</p>
                  <div className="space-y-1.5">
                    {templates.map(t => (
                      <button key={t.template_type} onClick={() => setCreateTemplate(createTemplate === t.template_type ? '' : t.template_type)}
                        className={`w-full p-2.5 rounded-lg border text-left transition-all ${
                          createTemplate === t.template_type ? 'border-[#9D4CDD]/40 bg-[#9D4CDD]/[0.06]' : 'border-white/[0.06] hover:border-white/[0.12]'
                        }`}>
                        <p className="text-white text-xs font-medium">{t.name}</p>
                        <p className="text-[#555] text-[10px]">{t.item_count} rules</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowCreate(false)} className="flex-1 py-2.5 rounded-lg bg-white/[0.04] text-[#888] text-sm">Cancel</button>
                <button onClick={createPack} disabled={!createName.trim()} className="flex-1 py-2.5 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold disabled:opacity-40" data-testid="confirm-create-pack">Create</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Packs List */}
      {loading ? (
        <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 bg-white/[0.03] rounded-xl animate-pulse" />)}</div>
      ) : packs.length > 0 ? (
        <div className="space-y-2">
          {packs.map(p => {
            const Icon = TYPE_ICONS[p.rule_pack_type] || BookOpen;
            const color = AUTHORITY_COLORS[p.authority_level] || '#6B7280';
            return (
              <div key={p.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4 flex items-center gap-3" data-testid={`pack-${p.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
                  <Icon size={20} weight="duotone" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-[#555] text-[10px]">{p.rule_pack_type?.replace(/_/g, ' ')} · {p.item_count} items · v{p.version}</p>
                </div>
                <button onClick={() => assignToShop(p.id)} className="px-3 py-1.5 rounded-lg bg-[#10B981]/10 text-[#10B981] text-xs font-medium hover:bg-[#10B981]/20 transition-colors">
                  Assign
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10">
          <BookOpen size={32} weight="duotone" className="text-[#333] mx-auto mb-3" />
          <p className="text-[#555] text-sm">No rule packs yet</p>
          <p className="text-[#333] text-xs mt-1">Create your first house rules pack!</p>
        </div>
      )}
    </div>
  );
}
